/**
 * usePromoConfig — live free-trial length for pricing copy.
 *
 * Pulls from GET /v2/pricing/promo via TanStack Query (5 min staleTime,
 * deduped across the app via the shared QueryClient). Returns the
 * hardcoded FALLBACK_PROMO_CONFIG on first paint and on API failure, so
 * callers always get a usable shape — no isLoading/isError plumbing.
 *
 * This is the generic trial LENGTH only. A logged-in user's actual expiry
 * date comes from GET /v2/me (`subscription.trialEndsAt`) via useUserInfo().
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { buildPromoConfig, FALLBACK_PROMO_CONFIG, type PromoConfig } from '@/lib/promo';

export function usePromoConfig(): PromoConfig {
  const { data } = useQuery({
    queryKey: ['promoConfig'],
    queryFn: async () => {
      const raw = await apiClient.getPricingPromo();
      return buildPromoConfig(raw.trialDays);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 0,
  });
  return data ?? FALLBACK_PROMO_CONFIG;
}
